import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Snackbar, SnackbarContent } from '@material-ui/core';

import {
  fetchStoreExceptionsHours,
  fetchStoreOpenHours,
  postStoreOpenHours,
  postStoreException,
} from '../../redux/actions/settings';
import { Loading } from '../../components/common';
import StoreHours from './StoreHours';
import { OpeningHoursState } from './opening-hours.state';
import {
  Wrapper,
  Col1,
  Title,
  DivButtonSave,
  SaveButton,
  Card,
  SubTitle,
  H3,
  Div,
  ErrorMessage,
  ErrorMessageTitle,
  ErrorMessageSubtitle,
} from './style';

export const OpeningHoursComponent = ({ storeId }) => {
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState({
    isOpen: false,
    message: '',
    background: '#434040',
  });

  const [state, dispatchLocally] = useReducer(
    OpeningHoursState.reducer,
    OpeningHoursState.initialState
  );
  const { operatingDays, exceptionDays } = state.entities[state.selectedId];

  useEffect(() => {
    dispatchLocally({ type: OpeningHoursState.actions.changeStatus, payload: 'loading' });

    const storeOpenHoursRequest = dispatch(
      fetchStoreOpenHours({
        storeId,
      })
    );
    const storeExceptionRequest = dispatch(
      fetchStoreExceptionsHours({
        storeId,
      })
    );

    Promise.all([storeOpenHoursRequest, storeExceptionRequest])
      .then(([storeOpenHoursResult, storeExceptionResult]) => {
        if (storeOpenHoursResult.success && storeExceptionResult.success) {
          dispatchLocally({
            type: OpeningHoursState.actions.loadSuccess,
            payload: {
              storeOpenHours: storeOpenHoursResult.data,
              exceptionsHours: storeExceptionResult.data,
            },
          });
        } else {
          dispatchLocally({ type: OpeningHoursState.actions.changeStatus, payload: 'error' });
        }
      })
      .catch(() =>
        dispatchLocally({ type: OpeningHoursState.actions.changeStatus, payload: 'error' })
      );
  }, [storeId, dispatch]);

  const handleChangeSelectModality = (payload) => {
    dispatchLocally({ type: OpeningHoursState.actions.selectModality, payload });
  };

  const handleToggleOperatingDay = (payload) => {
    dispatchLocally({ type: OpeningHoursState.actions.toggleDay, payload });
  };

  const handleOperatingDayTimeChange = (payload) => {
    dispatchLocally({ type: OpeningHoursState.actions.updateDayHour, payload });
  };

  const handleRemoveOperatingDayTime = (payload) => {
    dispatchLocally({ type: OpeningHoursState.actions.removeDayHour, payload });
  };

  const handleAddOperatingDayTime = (payload) => {
    dispatchLocally({ type: OpeningHoursState.actions.addDayHour, payload });
  };

  const handleAddExceptionDay = () => {
    dispatchLocally({ type: OpeningHoursState.actions.addExceptionDay });
  };

  const handleRemoveExceptionDay = (payload) => {
    dispatchLocally({ type: OpeningHoursState.actions.removeExceptionDay, payload });
  };

  const handleUpdateExceptionDay = (payload) => {
    dispatchLocally({ type: OpeningHoursState.actions.updateExceptionDay, payload });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason !== 'clickaway') {
      setSnackbar((state) => ({ ...state, isOpen: false }));
    }
  };

  const handleSave = async () => {
    dispatchLocally({ type: OpeningHoursState.actions.changeStatus, payload: 'pending' });

    const storeOpenHoursRequest = dispatch(
      postStoreOpenHours({
        data: {
          delivery: state.entities.delivery.operatingDays,
          pickup: state.entities.pickup.operatingDays,
        },
        storeId,
      })
    );

    const storeExceptionRequest = dispatch(
      postStoreException({
        data: {
          delivery: state.entities.delivery.exceptionDays,
          pickup: state.entities.pickup.exceptionDays,
        },
        storeId,
      })
    );

    const [storeOpenHoursResult, storeExceptionResult] = await Promise.all([
      storeOpenHoursRequest,
      storeExceptionRequest,
    ]);
    dispatchLocally({ type: OpeningHoursState.actions.changeStatus, payload: 'success' });

    const snackbarData =
      storeOpenHoursResult.success && storeExceptionResult.success
        ? { message: 'Configurações de horário salvas com sucesso!', backgroundColor: '#119C4A' }
        : {
            message: 'Erro ao salvar as configurações. Tente novamente.',
            backgroundColor: '#CD2A0B',
          };
    setSnackbar({ ...snackbarData, isOpen: true });
  };

  return (
    <Wrapper>
      <Col1>
        <Title>Configurações do horário de funcionamento</Title>
        {(() => {
          switch (state.status) {
            case 'loading':
              return <Loading />;
            case 'error':
              return (
                <ErrorMessage>
                  <ErrorMessageTitle>Ops! Falha ao carregar as configurações</ErrorMessageTitle>
                  <ErrorMessageSubtitle>
                    Houve um problema ao carregar as configurações do horário de funcionamento. Por
                    favor, recarregue a página ou tente mais tarde.
                  </ErrorMessageSubtitle>
                </ErrorMessage>
              );
            default:
              return (
                <>
                  <Card>
                    <SubTitle>
                      <Div
                        className={state.selectedId === 'delivery' ? 'selected' : ''}
                        onClick={() => handleChangeSelectModality('delivery')}
                      >
                        Delivery
                      </Div>
                      <Div
                        className={state.selectedId === 'pickup' ? 'selected' : ''}
                        onClick={() => handleChangeSelectModality('pickup')}
                      >
                        Retirada - Comer aqui
                      </Div>
                    </SubTitle>
                    <hr></hr>
                    <H3>Defina os horários de funcionamento</H3>
                    <StoreHours
                      operatingDays={operatingDays}
                      exceptionDays={exceptionDays}
                      handleOperatingDaysEnabledChange={handleToggleOperatingDay}
                      handleOperatingDayTimeChange={handleOperatingDayTimeChange}
                      handleAddOperatingDayTime={handleAddOperatingDayTime}
                      handleRemoveOperatingDayTime={handleRemoveOperatingDayTime}
                      handleAddExceptionDay={handleAddExceptionDay}
                      handleRemoveExceptionDay={handleRemoveExceptionDay}
                      handleUpdateExceptionDay={handleUpdateExceptionDay}
                    />
                  </Card>
                  <DivButtonSave>
                    <SaveButton
                      disabled={state.status === 'pending'}
                      className="primary"
                      onClick={() => handleSave()}
                    >
                      Salvar Alterações
                    </SaveButton>
                  </DivButtonSave>
                  <Snackbar
                    open={snackbar.isOpen}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                  >
                    <SnackbarContent
                      style={{
                        backgroundColor: snackbar.backgroundColor,
                      }}
                      message={snackbar.message}
                    />
                  </Snackbar>
                </>
              );
          }
        })()}
      </Col1>
    </Wrapper>
  );
};
